import { useEffect, useMemo, useState, useCallback } from 'react';
import { getAdminAnalytics } from '../../api/admin.api';
import type { AdminAnalyticsData, AdminSourcePerformance } from '../../api/admin.api';
import { useAdminConfig } from '../../hooks/useAdminConfig';

function copyToClipboard(text: string): boolean {
  try {
    navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export default function AdminMarketing() {
  const { features, config } = useAdminConfig();
  const [data, setData] = useState<AdminAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [sourceInput, setSourceInput] = useState('');
  const [preset, setPreset] = useState<'7d' | '30d' | 'all' | 'custom'>('30d');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [customSources, setCustomSources] = useState<string[]>([]);
  const [compactView, setCompactView] = useState<boolean>(() => window.innerWidth <= 900);

  useEffect(() => {
    const onResize = () => setCompactView(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('admin_marketing_sources');
      if (!raw) return;
      const parsed = JSON.parse(raw) as string[];
      if (Array.isArray(parsed)) {
        setCustomSources(parsed.filter((v) => typeof v === 'string' && v.trim().length > 0));
      }
    } catch {
      // ignore
    }
  }, []);

  const saveCustomSources = useCallback((next: string[]) => {
    setCustomSources(next);
    try {
      localStorage.setItem('admin_marketing_sources', JSON.stringify(next));
    } catch {
      // ignore
    }
  }, []);

  const periodParams = useMemo(() => {
    if (preset === 'all') return { period: 'all' as const };
    const now = new Date();
    const toDate = to || now.toISOString().slice(0, 10);
    if (preset === 'custom') {
      const fromDate = from || toDate;
      return { from: fromDate, to: toDate };
    }
    const days = preset === '7d' ? 6 : 29;
    const fromDateObj = new Date(now);
    fromDateObj.setDate(fromDateObj.getDate() - days);
    return { from: fromDateObj.toISOString().slice(0, 10), to: toDate };
  }, [preset, from, to]);

  useEffect(() => {
    setLoading(true);
    getAdminAnalytics(periodParams)
      .then(setData)
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Ошибка загрузки');
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [periodParams]);

  const marketing = data?.marketing ?? null;
  const trafficLinks = config?.trafficLinks ?? [];
  const sourcePerformance = marketing?.sourcePerformance ?? [];

  const sourcePerfMap = useMemo(() => {
    const m = new Map<string, AdminSourcePerformance>();
    for (const row of sourcePerformance) {
      m.set(String(row.source || 'unknown').toLowerCase(), row);
    }
    return m;
  }, [sourcePerformance]);

  const baseStartUrl = useMemo(() => {
    const first = trafficLinks[0]?.url || '';
    if (first.includes('?start=')) return first.split('?start=')[0];
    return 'https://t.me/beriohapkybot';
  }, [trafficLinks]);

  const sourceRows = useMemo(() => {
    const fromConfig = trafficLinks.map((l) => l.code.toLowerCase());
    const fromPerf = sourcePerformance.map((s) => String(s.source || '').toLowerCase());
    const fromCustom = customSources.map((s) => s.toLowerCase());
    const merged = Array.from(new Set([...fromConfig, ...fromPerf, ...fromCustom])).filter(Boolean);
    return merged.map((code) => {
      const perf = sourcePerfMap.get(code);
      return {
        code,
        link: `${baseStartUrl}?start=${encodeURIComponent(code)}`,
        transitionsPeriod: perf?.transitionsPeriod ?? 0,
        transitionsTotal: perf?.transitionsTotal ?? 0,
        ordersPeriod: perf?.ordersPeriod ?? 0,
        ordersTotal: perf?.ordersTotal ?? 0,
        revenuePeriod: perf?.revenuePeriod ?? 0,
        revenueTotal: perf?.revenueTotal ?? 0,
      };
    });
  }, [trafficLinks, sourcePerformance, customSources, sourcePerfMap, baseStartUrl]);

  const handleCopy = useCallback((code: string, url: string) => {
    if (copyToClipboard(url)) {
      setCopiedId(code);
      setTimeout(() => setCopiedId(null), 1500);
    }
  }, []);

  if (loading && !data) {
    return <div style={{ color: 'var(--text-secondary)' }}>Загрузка…</div>;
  }
  if (error) {
    return <div className="admin-error">{error}</div>;
  }
  if (!marketing) {
    return <div style={{ color: 'var(--text-secondary)' }}>Нет данных. Все маркетинговые метрики рассчитываются на сервере.</div>;
  }

  const cohort = marketing.cohortAnalysis ?? [];
  const funnel = marketing.funnel ?? [];
  const noOrdersPercent = marketing.clientsWithoutOrdersPercent ?? 0;
  const cac = marketing.cacPlaceholder;

  const handleGenerate = () => {
    const normalized = sourceInput
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_-]/g, '');
    if (!normalized) return;
    if (!customSources.includes(normalized)) {
      saveCustomSources([...customSources, normalized]);
    }
    setSourceInput('');
  };

  return (
    <div className="admin-page">
      <div className="admin-filters" style={{ marginBottom: '14px' }}>
        <select className="admin-input" value={preset} onChange={(e) => setPreset(e.target.value as '7d' | '30d' | 'all' | 'custom')}>
          <option value="7d">Последние 7 дней</option>
          <option value="30d">Последние 30 дней</option>
          <option value="all">За всё время</option>
          <option value="custom">Свой период</option>
        </select>
        {preset === 'custom' && (
          <>
            <input className="admin-input admin-input-date" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <input className="admin-input admin-input-date" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </>
        )}
      </div>

      <div className="admin-card" style={{ marginBottom: '20px' }}>
        <div className="admin-card-title">Генератор ссылок</div>
        <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: 0, marginBottom: '12px' }}>
          Созданные ссылки сохраняются. Клик по ссылке копирует её в буфер.
        </p>
        <div className="admin-filters" style={{ marginBottom: 0 }}>
          <input
            className="admin-input"
            placeholder="Например: vk_group"
            value={sourceInput}
            onChange={(e) => setSourceInput(e.target.value)}
          />
          <button type="button" className="admin-btn admin-btn-primary" onClick={handleGenerate}>
            Сгенерировать
          </button>
        </div>
      </div>

      <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
        Все показатели (LTV, воронка, когорты) считаются на сервере по заказам и пользователям.
      </div>

      {trafficLinks.length > 0 && (
        <div className="admin-card" style={{ marginBottom: '20px' }}>
          <div className="admin-card-title">Ссылки для каналов</div>
          <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: 0, marginBottom: '12px' }}>
            Используйте эти ссылки в постах, рекламе и QR-кодах. Источник сохранится при первом заказе и будет виден в отчётах.
          </p>
          <div className="admin-traffic-links">
            {trafficLinks.map((link) => (
              <div key={link.code} className="admin-traffic-link-row">
                <div className="admin-traffic-link-info">
                  <span className="admin-traffic-link-label">{link.label}</span>
                  {link.hint && <span className="admin-traffic-link-hint">{link.hint}</span>}
                </div>
                <div className="admin-traffic-link-url-wrap">
                  <code className="admin-traffic-link-url">{link.url}</code>
                  <button
                    type="button"
                    className="admin-btn admin-btn-secondary admin-btn-sm"
                    onClick={() => handleCopy(link.code, link.url)}
                    title="Копировать"
                  >
                    {copiedId === link.code ? '✓ Скопировано' : 'Копировать'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="admin-card" style={{ marginBottom: '20px' }}>
        <div className="admin-card-title">Сохраненные ссылки и переходы</div>
        {compactView ? (
          <div style={{ display: 'grid', gap: 10 }}>
            {sourceRows.map((row) => (
              <div key={row.code} className="admin-card">
                <div style={{ fontWeight: 700 }}>{row.code}</div>
                <div><a href={row.link} onClick={(e) => { e.preventDefault(); handleCopy(row.code, row.link); }} className="admin-link">{row.link}</a></div>
                <div>Переходы (период): {row.transitionsPeriod}</div>
                <div>Переходы (всего): {row.transitionsTotal}</div>
                <div>Заказов: {row.ordersPeriod}</div>
                <div>Выручка: {row.revenuePeriod.toLocaleString('ru-RU')} ₽</div>
              </div>
            ))}
          </div>
        ) : (
        <div className="admin-table-wrap admin-sources-links-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="admin-th">Источник</th>
                <th className="admin-th">Ссылка</th>
                <th className="admin-th admin-num">Переходы (период)</th>
                <th className="admin-th admin-num">Переходы (всего)</th>
                <th className="admin-th admin-num">Заказов</th>
                <th className="admin-th admin-num">Выручка</th>
              </tr>
            </thead>
            <tbody>
              {sourceRows.map((row) => (
                <tr key={row.code} className="admin-table-row">
                  <td className="admin-td"><strong>{row.code}</strong></td>
                  <td className="admin-td admin-td-link">
                    <a href={row.link} onClick={(e) => { e.preventDefault(); handleCopy(row.code, row.link); }} className="admin-link">
                      {row.link}
                    </a>
                  </td>
                  <td className="admin-td admin-num">{row.transitionsPeriod}</td>
                  <td className="admin-td admin-num">{row.transitionsTotal}</td>
                  <td className="admin-td admin-num">{row.ordersPeriod}</td>
                  <td className="admin-td admin-num">{row.revenuePeriod.toLocaleString('ru-RU')} ₽</td>
                </tr>
              ))}
              {sourceRows.length === 0 && (
                <tr>
                  <td className="admin-td" colSpan={6}>Источники пока не добавлены.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        <div className="admin-card">
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Повторные покупки</div>
          <div style={{ fontSize: '20px', fontWeight: 600 }}>{marketing.repeatPurchasesPercent.toFixed(1)}%</div>
        </div>
        <div className="admin-card">
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Ср. заказов на клиента</div>
          <div style={{ fontSize: '20px', fontWeight: 600 }}>{marketing.avgOrdersPerClient.toFixed(2)}</div>
        </div>
        <div className="admin-card">
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>LTV</div>
          <div style={{ fontSize: '20px', fontWeight: 600 }}>{marketing.ltv.toLocaleString('ru-RU')} ₽</div>
        </div>
        <div className="admin-card">
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>% без заказов</div>
          <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>Доля зарегистрированных, кто ни разу не оформил заказ</div>
          <div style={{ fontSize: '20px', fontWeight: 600 }}>{noOrdersPercent.toFixed(1)}%</div>
        </div>
        {cac != null && (
          <div className="admin-card">
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>CAC (расходы)</div>
            <div style={{ fontSize: '20px', fontWeight: 600 }}>{cac > 0 ? `${cac.toLocaleString('ru-RU')} ₽` : '—'}</div>
          </div>
        )}
      </div>

      {features?.funnel !== false && funnel.length > 0 && (
        <div className="admin-card" style={{ marginBottom: '16px' }}>
          <div className="admin-card-title">Воронка</div>
          <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '10px', marginTop: '0' }}>
            Доля пользователей на каждом этапе пути до заказа.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {funnel.map((step) => (
              <div key={step.label} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ width: '140px', fontSize: '13px' }}>{step.label}</span>
                <div
                  style={{
                    flex: 1,
                    height: '24px',
                    borderRadius: '6px',
                    backgroundColor: 'var(--color-accent-light)',
                    width: `${step.percent}%`,
                    minWidth: step.percent > 0 ? '24px' : '0',
                  }}
                />
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)', minWidth: '80px' }}>
                  {step.count} ({step.percent.toFixed(1)}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {features?.cohorts !== false && cohort.length > 0 && (
        <div className="admin-card" style={{ marginBottom: '16px' }}>
          <div className="admin-card-title">Когортный анализ (% повторный заказ по месяцу регистрации)</div>
          <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '10px', marginTop: '0' }}>
            Доля клиентов, сделавших повторный заказ, по месяцу первой регистрации.
          </p>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th className="admin-th">Месяц</th>
                  <th className="admin-th admin-num">Регистраций</th>
                  <th className="admin-th admin-num">Повторных</th>
                  <th className="admin-th admin-num">%</th>
                </tr>
              </thead>
              <tbody>
                {cohort.map((row) => (
                  <tr key={row.cohortMonth}>
                    <td className="admin-td">{row.cohortMonth}</td>
                    <td className="admin-td admin-num">{row.cohortSize}</td>
                    <td className="admin-td admin-num">{row.repeatCount}</td>
                    <td className="admin-td admin-num">{row.repeatPercent.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {marketing.usersBySourcePercent?.length ? (
        <div className="admin-card">
          <div className="admin-card-title">% пользователей по источникам</div>
          <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '0', marginBottom: '10px' }}>
            Источник берётся из первого заказа или из start_param при переходе в бота (например <code style={{ fontSize: '11px' }}>t.me/YourBot?start=rsya</code>, <code style={{ fontSize: '11px' }}>start=vk</code>, <code style={{ fontSize: '11px' }}>start=qr</code>). Чтобы видеть РСЯ, ВК, QR и др., используйте разные start-параметры в ссылках.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {marketing.usersBySourcePercent.map((s) => (
              <div key={s.source} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ width: '120px', fontSize: '13px' }}>{s.source}</span>
                <div
                  style={{
                    flex: 1,
                    height: '20px',
                    borderRadius: '4px',
                    backgroundColor: 'var(--color-accent-light)',
                    width: `${s.percent}%`,
                    minWidth: s.percent > 0 ? '20px' : '0',
                  }}
                />
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  {s.count} ({s.percent.toFixed(1)}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
